import type {GuardarPrefijos, ListaDePrefijos} from '@cnc/contracts';
import {useEffect, useState} from 'react';
import {Link} from 'react-router';
import {useGuardarPrefijos, usePrefijos} from '../api/seguimiento';
import {AYUDA_PREFIJOS as A} from '../componentes/ayuda-general';
import {NotaDeAyuda} from '../componentes/nota-de-ayuda';
import {usePermisos} from '../componentes/sesion-contexto';
import {tr} from '../i18n/idioma';
import {numero} from '../utilidades/formato';

type Fila = {prefijo: string; exportador: string; activo: boolean; notas: string; nuevo: boolean};

// Exportador por prefijo del codigo de exportacion (GLC-, MA-, TSM-...).
// Los nombres se escriben aqui y quedan en la base de datos, no en el codigo.
export function PaginaPrefijos() {
  const permisos = usePermisos();
  const consulta = usePrefijos();
  const guardar = useGuardarPrefijos();
  const [filas, setFilas] = useState<Fila[]>([]);
  const [mensaje, setMensaje] = useState<string | null>(null);

  useEffect(() => {
    if (consulta.data) setFilas(desdeLista(consulta.data));
  }, [consulta.data]);

  const cambiar = (indice: number, cambios: Partial<Fila>) =>
    setFilas(actual => actual.map((f, i) => (i === indice ? {...f, ...cambios} : f)));
  const agregar = (prefijo = '') =>
    setFilas(actual => [...actual, {prefijo, exportador: '', activo: true, notas: '', nuevo: true}]);
  const quitarNueva = (indice: number) => setFilas(actual => actual.filter((_, i) => i !== indice));

  const enviar = async () => {
    setMensaje(null);
    const validas = filas.filter(f => f.prefijo.trim() && f.exportador.trim());
    if (!validas.length) {
      setMensaje(tr('Escribe al menos un prefijo con su exportador.', 'Enter at least one prefix with its exporter.'));
      return;
    }
    const cuerpo: GuardarPrefijos = {
      prefijos: validas.map(f => ({
        prefijo: f.prefijo.trim().toUpperCase(),
        exportador: f.exportador.trim(),
        activo: f.activo,
        notas: f.notas.trim() || null,
      })),
    };
    try {
      await guardar.mutateAsync(cuerpo);
      setMensaje(
        tr(
          'Guardado. La tabla de seguimiento ya muestra los exportadores.',
          'Saved. The tracking table now shows the exporters.',
        ),
      );
    } catch (error) {
      setMensaje(error instanceof Error ? error.message : tr('No se pudo guardar.', 'Could not save.'));
    }
  };

  return (
    <section className="max-w-4xl">
      <Link to="/configuracion-de-campos" className="text-sm text-verde hover:underline">
        {tr('← Volver a configuración de campos', '← Back to field settings')}
      </Link>
      <h1 className="mt-3 text-2xl font-semibold text-verde-oscuro">
        {tr('Exportadores por prefijo', 'Exporters by prefix')}
      </h1>
      <p className="text-sm text-apagado">
        {tr(
          'Las exportaciones de terceros no traen exportador en el archivo de seguimiento. Aquí se define a quién corresponde cada prefijo del código (por ejemplo GLC-01 → el exportador del prefijo GLC). Si un código empieza por varios prefijos, manda el más largo.',
          'Third-party exports have no exporter in the tracking file. Here you define who each code prefix belongs to (for example GLC-01 → the exporter of prefix GLC). If a code starts with several prefixes, the longest one wins.',
        )}
      </p>
      {consulta.isPending && <p className="mt-4 text-apagado">{tr('Consultando…', 'Loading…')}</p>}
      {consulta.isError && <p className="mt-4 text-sm text-alerta">{consulta.error.message}</p>}
      {consulta.data && (
        <div className="mt-4 space-y-4">
          <div className="overflow-auto rounded-lg border border-linea bg-white">
            <table className="w-full text-sm">
              <thead className="bg-papel">
                <tr className="text-left text-xs uppercase tracking-wide text-apagado">
                  <th className="px-3 py-2 font-semibold">
                    {tr('Prefijo', 'Prefix')}
                    <NotaDeAyuda texto={A.prefijo} />
                  </th>
                  <th className="px-3 py-2 font-semibold">
                    {tr('Exportador', 'Exporter')}
                    <NotaDeAyuda texto={A.exportador} />
                  </th>
                  <th className="px-3 py-2 text-right font-semibold">
                    {tr('Exportaciones', 'Exports')}
                    <NotaDeAyuda texto={A.exportaciones} />
                  </th>
                  <th className="px-3 py-2 font-semibold">
                    {tr('Activo', 'Active')}
                    <NotaDeAyuda texto={A.activo} />
                  </th>
                  <th className="px-3 py-2 font-semibold">
                    {tr('Notas', 'Notes')}
                    <NotaDeAyuda texto={A.notas} />
                  </th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {filas.map((fila, indice) => (
                  <tr key={`${fila.prefijo}-${indice}`} className="border-t border-linea/70">
                    <td className="px-3 py-1.5">
                      <input
                        value={fila.prefijo}
                        onChange={e => cambiar(indice, {prefijo: e.target.value.toUpperCase()})}
                        readOnly={!fila.nuevo}
                        maxLength={10}
                        className={`w-24 rounded-md border border-linea px-2 py-1 font-mono ${fila.nuevo ? '' : 'bg-papel'}`}
                        placeholder="GLC"
                      />
                    </td>
                    <td className="px-3 py-1.5">
                      <input
                        value={fila.exportador}
                        onChange={e => cambiar(indice, {exportador: e.target.value})}
                        list="exportadores-conocidos"
                        className="w-full min-w-48 rounded-md border border-linea px-2 py-1"
                        placeholder={tr('Nombre de la empresa', 'Company name')}
                      />
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">
                      {fila.nuevo
                        ? '—'
                        : numero(consulta.data.prefijos.find(p => p.prefijo === fila.prefijo)?.exportaciones)}
                    </td>
                    <td className="px-3 py-1.5">
                      <input
                        type="checkbox"
                        checked={fila.activo}
                        onChange={e => cambiar(indice, {activo: e.target.checked})}
                        className="accent-verde"
                      />
                    </td>
                    <td className="px-3 py-1.5">
                      <input
                        value={fila.notas}
                        onChange={e => cambiar(indice, {notas: e.target.value})}
                        className="w-full rounded-md border border-linea px-2 py-1"
                      />
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      {fila.nuevo && (
                        <button
                          type="button"
                          onClick={() => quitarNueva(indice)}
                          className="text-xs text-apagado hover:text-alerta"
                        >
                          {tr('Quitar', 'Remove')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {!filas.length && (
                  <tr>
                    <td colSpan={6} className="px-3 py-6 text-center text-apagado">
                      {tr('Todavía no hay prefijos definidos.', 'No prefixes defined yet.')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <datalist id="exportadores-conocidos">
              {consulta.data.exportadoresConocidos.map(nombre => (
                <option key={nombre} value={nombre} />
              ))}
            </datalist>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {permisos.editar ? (
              <>
                <button
                  type="button"
                  onClick={() => agregar()}
                  className="rounded-md border border-linea bg-white px-3 py-1.5 text-sm hover:bg-papel"
                >
                  {tr('+ Agregar prefijo', '+ Add prefix')}
                </button>
                <button
                  type="button"
                  onClick={() => void enviar()}
                  disabled={guardar.isPending}
                  className="rounded-md bg-verde px-3 py-1.5 text-sm font-medium text-white hover:bg-verde-oscuro disabled:opacity-50"
                >
                  {guardar.isPending ? tr('Guardando…', 'Saving…') : tr('Guardar', 'Save')}
                </button>
              </>
            ) : (
              <span className="text-sm text-apagado">
                {tr(
                  'Tu rol es de consulta: la tabla se ve pero no se cambia.',
                  'Your role is read-only: you can see the table but not change it.',
                )}
              </span>
            )}
            {mensaje && <span className="text-sm text-apagado">{mensaje}</span>}
          </div>
          {consulta.data.sinAsignar.length > 0 && (
            <div className="rounded-lg border border-dorado/40 bg-dorado-claro p-4 text-sm">
              <div className="font-medium">
                {tr(
                  'Prefijos que aparecen en los códigos y aún no tienen exportador',
                  'Prefixes that appear in the codes and have no exporter yet',
                )}
                <NotaDeAyuda texto={A.sinAsignar} />
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {consulta.data.sinAsignar.map(s => (
                  <button
                    key={s.prefijo}
                    type="button"
                    onClick={() => agregar(s.prefijo)}
                    className="rounded-full border border-linea bg-white px-2.5 py-1 text-xs hover:border-verde"
                    title={tr('Agregar este prefijo a la tabla', 'Add this prefix to the table')}
                  >
                    {s.prefijo} · {numero(s.exportaciones)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

const desdeLista = (lista: ListaDePrefijos): Fila[] =>
  lista.prefijos.map(p => ({
    prefijo: p.prefijo,
    exportador: p.exportador,
    activo: p.activo,
    notas: p.notas ?? '',
    nuevo: false,
  }));
